import { LocationSchema, ErrorMessage } from 'components/Form'
import { TextInput, CountryInput, StateInput } from 'components/UserInputs'
import { Form, Formik, FormikProps } from 'formik'
import { useBoolean, useHandleEscapeKey } from 'hooks'
import { useEffect, useMemo, useCallback, useRef } from 'react'
import { Box, Flex, Button } from 'theme-ui'
import {
  CreateLocationProps,
  LocationFormikOnlyValues,
  TeachingLocation,
} from 'types/location'
import { lazy } from 'yup'
import { Loading } from 'components'
import { useCreateLocation } from 'hooks/locationHooks/useCreateLocation'
import { FormModal } from './FormModal'
import { withoutFalsy } from 'utils/helpers'
import { preFillFormWithPlace, validatePlaceData } from 'utils/googlePlaces'
import { PlacesAutocompleteInput } from 'components/Places/PlacesAutocompleteInput'
import { getFromLocalizedString } from 'utils/getFromLocalizedString'

type FormValues = CreateLocationProps & LocationFormikOnlyValues

export type CreateLocationInputProps = {
  countryCode?: string
  onLocationCreated: (id: string) => void
  onError?: (error: string) => void
}

export const CreateLocationInput = ({
  countryCode,
  onLocationCreated,
}: CreateLocationInputProps) => {
  const [isModalVisible, openModal, closeModal] = useBoolean(false)

  const locationCreated = ({ id }: TeachingLocation) => {
    closeModal()
    onLocationCreated(id)
  }

  return (
    <Box>
      <LocationCreationModal
        countryCode={countryCode}
        onLocationCreated={locationCreated}
        handleCancel={closeModal}
        show={isModalVisible}
      />
      <Box
        onClick={openModal}
        variant="text.accent"
        sx={{
          display: 'inline',
          cursor: 'pointer',
        }}
      >
        Click here
      </Box>{' '}
      if you can't find your location in the search results
    </Box>
  )
}

export const LocationCreationModal = ({
  countryCode,
  onLocationCreated,
  handleCancel,
  show,
}: {
  countryCode?: string
  onLocationCreated: (location: TeachingLocation) => void
  handleCancel: () => void
  show: boolean
}) => {
  const initialValues = useMemo(() => {
    return {
      countryCode: countryCode ?? '',
      name: '',
      addressLine: '',
      city: '',
      type: 'TMCenter',
    } as FormValues
  }, [countryCode])

  useHandleEscapeKey(handleCancel)

  const lastCreatedLocationId = useRef<string>('')
  const [create, createdLocation, isLoading, error] = useCreateLocation()
  useEffect(() => {
    const lastCreatedId = lastCreatedLocationId.current
    if (createdLocation && createdLocation.id !== lastCreatedId) {
      lastCreatedLocationId.current = createdLocation.id
      onLocationCreated(createdLocation)
    }
  }, [lastCreatedLocationId, createdLocation, onLocationCreated])

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const newLocation = withoutFalsy(values)
      validatePlaceData(newLocation)
      delete newLocation.__country
      delete newLocation.__googleData
      create(newLocation as CreateLocationProps)
    },
    [create]
  )

  return show ? (
    <FormModal
      heading="Can't find your TM center?"
      SubHeading={
        <ol style={{ paddingLeft: 0 }}>
          <Box
            sx={{
              lineHeight: '22px',
              color: 'lightGray',
              fontSize: 2,
              width: [333, 568],
              pl: [16, 0],
              '& > *': {
                py: 1,
              },
            }}
          >
            <li>Please check the spelling of the city.</li>

            <li>Ensure that your teaching country is set correctly.</li>

            <li>
              Your location may not be in our national list yet. Not to worry,
              you can add it below.
            </li>
          </Box>
        </ol>
      }
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={() => {
          return lazy(() => {
            const schema = LocationSchema
            return schema
          })
        }}
      >
        {({
          errors,
          values,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }: FormikProps<FormValues>) => {
          return (
            <Form autoComplete="off" autoCorrect="off" onSubmit={handleSubmit}>
              <Box
                data-cy="create-location-modal"
                variant="forms.locationForm"
                sx={{
                  '& > *': {
                    mt: '-6px',
                  },
                }}
              >
                <TextInput
                  name="name"
                  label="Center name"
                  value={getFromLocalizedString(values.name)}
                  error={errors.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={100}
                />
                <PlacesAutocompleteInput
                  name="addressLine"
                  label="Street address"
                  value={values.addressLine}
                  countryCode={values.countryCode}
                  error={touched.addressLine ? errors.addressLine : undefined}
                  onPlaceSelected={(place) => {
                    preFillFormWithPlace(setFieldValue, place)
                  }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={101}
                />
                <TextInput
                  name="city"
                  label="City"
                  value={values.city}
                  error={touched.city ? errors.city : undefined}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={102}
                />
                <Flex
                  sx={{
                    justifyContent: 'space-between',
                    '& > * ': {
                      flex: '0 0 150px',
                    },
                  }}
                >
                  <Box>
                    <StateInput
                      name="state"
                      label="State/Province"
                      countryCode={values.countryCode}
                      value={values.state}
                      error={touched.state ? errors.state : undefined}
                      setFieldValue={setFieldValue}
                      onBlur={handleBlur}
                      tabIndex={103}
                    />
                  </Box>
                  <Box>
                    <TextInput
                      name="zip"
                      label="Zip/Postal code"
                      value={values.zip}
                      error={touched.zip ? errors.zip : undefined}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      tabIndex={104}
                    />
                  </Box>
                </Flex>
                <CountryInput
                  label="Teaching country"
                  name="countryCode"
                  value={values.countryCode}
                  setFieldValue={setFieldValue}
                  error={touched.countryCode ? errors.countryCode : undefined}
                  onBlur={handleBlur}
                  tabIndex={105}
                />

                <TextInput
                  name="specialInstructions"
                  label="Special instructions"
                  placeholder="To help a student find this location"
                  value={getFromLocalizedString(
                    values.specialInstructions ?? ''
                  )}
                  error={
                    touched.specialInstructions
                      ? errors.specialInstructions
                      : undefined
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={106}
                />
                <Flex variant="forms.buttons.container" sx={{ mt: 20, mb: 5 }}>
                  <Button
                    type="submit"
                    variant="forms.buttons.primary"
                    disabled={
                      Object.keys(errors).length > 0 ||
                      Object.keys(touched).length === 0 ||
                      isLoading
                    }
                  >
                    {isLoading ? <Loading /> : 'Save'}
                  </Button>
                  <Button
                    variant="forms.buttons.secondary"
                    type="button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Flex>
                <ErrorMessage sx={{ mb: 40 }}>
                  {error && <span>{error}</span>}
                </ErrorMessage>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </FormModal>
  ) : null
}
